import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPersonSwimming, faSpinner, faRotate, faChevronDown, faCloudDownload, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { useSwimmerAPI } from '../context/SwimmerAPIContext';
import { compareEvents, parseEventName, sortStrokeValues, collectStrokes, collectDistances } from '../../services/utils/EventOrdering';
import { COUNTIES } from '../../services/utils/counties';
import { REGIONS } from '../../services/utils/regions';
import { detectClubCode, getCountyCodeForClub, getRegionCodeForClub } from '../../services/utils/clubs';
import { STROKE_IDS } from '../../services/utils/strokeIds';
import { calculateAge, formatAgeGroupLabel, resolveCombinedAgeGroup } from '../../services/utils/ageCalculation';
import { useCollapsible } from '../hooks/useCollapsible';
import { useCourseFilter, useGroupBy, groupLabel, courseLabel } from '../hooks/useEventFilters';
import { SWIM_FACTS } from '../../services/utils/facts';
import { EventFilterBar, SegmentedToggle } from './filters';
import CombinedAgeGroupEditor from './controls/CombinedAgeGroupEditor';
const ageModeKey = (tiref) => `rankings-age-mode:${tiref}`;
// Replaced by the per-swimmer combined age group. Swept once so the old golbal toggle does not
// linger in every existing browser.
localStorage.removeItem('leaderboard-forecast-mode');
/**
 * Order the mobile ranking cards read in: most local first, so the pool's swimmer
 * actually competes in come before ones they are a needle in.
 */
const CARD_LEVELS = ['O', 'R', 'C', 'N'];
/* Desktop column order, which must match the table headings. */
const CARD_LEVELS_DESKTOP = ['C', 'R', 'O', 'N'];
/** Positions at or inside this get the highlighted pill. */
const STRONG_POSITION = 10;
const formatTotal = (total, truncated) => truncated ? `${total - 1}+` : String(total);
function dropRankingsForOtherAges(map, expected) {
    const result = new Map();
    for (const [key, cached] of Object.entries(map ?? {})) {
        const kept = Object.fromEntries(Object.entries(cached).filter(([, level]) => !level?.ageGroup || level.ageGroup === expected));
        if (Object.keys(kept).length)
            result.set(key, kept);
    }
    return result;
}
function buildRankingUrl(event, course, sex, ageGroup, level, countyCode, regionCode, clubCode) {
    const strokeId = STROKE_IDS[event];
    if (!strokeId || !ageGroup)
        return undefined;
    const pool = course === '50m' ? 'L' : 'S';
    const date = encodeURIComponent(`31/12/${new Date().getFullYear()}`);
    let url = `https://www.swimmingresults.org/12months/last12.php?Pool=${pool}&Stroke=${strokeId}&Sex=${sex}&AgeGroup=${ageGroup}&date=${date}&StartNumber=1&RecordsToView=100`;
    if (level === 'C') {
        url += `&TargetNationality=P&TargetRegion=P&Level=C&TargetCounty=${countyCode}&TargetClub=XXXX`;
    }
    else if (level === 'R') {
        url += `&TargetNationality=X&TargetRegion=${regionCode ?? 'P'}&Level=D&TargetCounty=XXXX&TargetClub=XXXX`;
    }
    else if (level === 'O') {
        url += `&TargetNationality=P&TargetRegion=P&TargetCounty=XXXX&Level=O&TargetClub=${clubCode ?? 'XXXX'}`;
    }
    else {
        url += '&Level=N&TargetNationality=X&TargetRegion=P&TargetCounty=XXXX&TargetClub=XXXX';
    }
    return url;
}
const RankingsLoadingOverlay = ({ eventCount, ageLabel, ageCount }) => {
    const [factIndex, setFactIndex] = useState(() => Math.floor(Math.random() * SWIM_FACTS.length));
    const [factVisible, setFactVisible] = useState(true);
    useEffect(() => {
        const interval = setInterval(() => {
            setFactVisible(false);
            setTimeout(() => {
                setFactIndex(i => {
                    let next;
                    do {
                        next = Math.floor(Math.random() * SWIM_FACTS.length);
                    } while (next === i);
                    return next;
                });
            }, 380);
            setTimeout(() => {
                setFactVisible(true);
            }, 420);
        }, 8000);
        return () => {
            clearInterval(interval);
            setFactVisible(false);
        };
    }, []);
    return (React.createElement("div", { style: {
            position: 'fixed',
            inset: 0,
            background: 'color-mix(in srgb, var(--bg-primary) 94%, transparent)',
            backdropFilter: 'blur(3px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99,
            gap: '1.25rem',
            padding: '2rem 1.5rem',
        } },
        React.createElement("div", { style: { position: 'relative', width: '100%', maxWidth: '380px', height: '52px', overflow: 'hidden' } },
            React.createElement("div", { style: { position: 'absolute', top: '25px', left: 0, right: 0, height: '2px', background: 'var(--primary)', opacity: 0.2, borderRadius: '1px' } }),
            React.createElement("div", { style: { position: 'absolute', top: '31px', left: 0, right: 0, height: '2px', background: 'var(--primary)', opacity: 0.12, borderRadius: '1px' } }),
            React.createElement("div", { style: {
                    position: 'absolute',
                    top: '14px',
                    fontSize: '1.5rem',
                    color: 'var(--primary)',
                    animation: 'swimAcross 2.6s linear infinite',
                } },
                React.createElement(FontAwesomeIcon, { icon: faPersonSwimming }))),
        React.createElement("div", { style: { textAlign: 'center' } },
            React.createElement("div", { style: { fontSize: '1.05rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.3rem' } }, "Fetching rankings\u2026"),
            React.createElement("div", { style: { fontSize: '0.82rem', color: 'var(--text-secondary)' } },
                "Checking ",
                eventCount,
                " event",
                eventCount !== 1 ? 's' : '',
                " at age ",
                ageLabel,
                " across club, county, regional & national levels",
                ageCount > 1 && React.createElement(React.Fragment, null,
                    " - ",
                    ageCount,
                    " age lists each, so this one takes longer"))),
        React.createElement("div", { style: { width: '100%', maxWidth: '280px', height: '3px', background: 'var(--bg-secondary)', borderRadius: '2px', overflow: 'hidden' } },
            React.createElement("div", { style: {
                    height: '100%',
                    width: '30%',
                    background: 'var(--primary)',
                    borderRadius: '2px',
                    animation: 'rankingsSlide 1.7s ease-in-out infinite',
                } })),
        React.createElement("div", { style: {
                maxWidth: '340px',
                textAlign: 'center',
                fontSize: '0.82rem',
                color: 'var(--text-secondary)',
                fontStyle: 'italic',
                lineHeight: 1.55,
                minHeight: '3em',
                opacity: factVisible ? 1 : 0,
                transition: 'opacity 0.35s ease',
            } },
            "\"",
            SWIM_FACTS[factIndex],
            "\"")));
};
const Rankings = ({ swimmerData, loading, showToast, onRankingsSaved, onCombinedAgeGroupChange, onSwimmerDataChange, selectedStrokes = [], onSelectedStrokesChange, selectedDistances = [], onSelectedDistancesChange }) => {
    const api = useSwimmerAPI();
    // Swimmer demographics used for ranking URL construction
    const swimmerAge = swimmerData.birthYear ? calculateAge(swimmerData.birthYear) : 0;
    const swimmerSex = swimmerData.gender?.toUpperCase().includes('FEMALE') ? 'F' : 'M';
    const ownAgeLabel = formatAgeGroupLabel([swimmerAge]);
    const combinedAgeGroup = resolveCombinedAgeGroup(swimmerData.combinedAgeGroup, swimmerAge);
    const hasCombined = !!combinedAgeGroup;
    // A group saved in a previous year may no longer coantain the swimmer's ranking year.
    const combinedAgedOut = !!swimmerData.combinedAgeGroup?.length && !hasCombined;
    const [ownRankings, setOwnRankings] = useState(() => dropRankingsForOtherAges(swimmerData.rankings, ownAgeLabel));
    const [combinedRankingsData, setCombinedRankingsData] = useState(() => new Map(Object.entries(swimmerData.combinedRankings ?? {})));
    const [courseFilter] = useCourseFilter();
    const [collapsedCourses, toggleCourse] = useCollapsible();
    const [loadingEvents, setLoadingEvents] = useState(new Set());
    const [fetchingAll, setFetchingAll] = useState(false);
    const [ageMode, setAgeMode] = useState(() => localStorage.getItem(ageModeKey(swimmerData.tiref)) === 'combined' ? 'combined' : 'own');
    const [groupBy, setGroupBy] = useGroupBy();
    const countyCode = swimmerData.selectedCountyCode ?? getCountyCodeForClub(swimmerData.club) ?? 'KNTQ';
    const regionCode = swimmerData.selectedRegionCode ?? getRegionCodeForClub(swimmerData.club);
    const selectedCountyName = COUNTIES.find(c => c.code === countyCode)?.name ?? countyCode;
    const selectedRegionName = REGIONS.find(r => r.code === regionCode)?.name ?? 'Region';
    const swimmerClubCode = detectClubCode(swimmerData.club);
    const swimmerClubName = swimmerData.club;
    const levelName = {
        O: swimmerClubName || 'Club',
        R: selectedRegionName,
        C: selectedCountyName,
        N: 'National',
    };
    // Combined mode has no control to leave it once the group goes away, so never stay there.
    useEffect(() => {
        if (!hasCombined && ageMode === 'combined') {
            localStorage.setItem(ageModeKey(swimmerData.tiref), 'own');
            setAgeMode('own');
        }
    }, [hasCombined, ageMode, swimmerData.tiref]);
    const rankings = ageMode === 'combined' ? combinedRankingsData : ownRankings;
    const activeAges = ageMode === 'combined' && combinedAgeGroup ? combinedAgeGroup : [swimmerAge];
    const activeAgeLabel = formatAgeGroupLabel(activeAges);
    // Sync rankings when swimmerData changes (e.g., when loading a saved swimmer)
    useEffect(() => {
        setOwnRankings(dropRankingsForOtherAges(swimmerData.rankings, ownAgeLabel));
        setCombinedRankingsData(new Map(Object.entries(swimmerData.combinedRankings ?? {})));
    }, [swimmerData.tiref, swimmerData.rankings, swimmerData.combinedRankings, ownAgeLabel]);
    // Each swimmer remembers their own mode: the group they'd be switching into is theirs alone.
    useEffect(() => {
        setAgeMode(localStorage.getItem(ageModeKey(swimmerData.tiref)) === 'combined' ? 'combined' : 'own');
    }, [swimmerData.tiref]);
    // Get unique events (event + course combination)
    const events = swimmerData.times.map(time => ({
        event: time.event,
        course: time.course,
        time: time.time,
        date: time.date,
        sourceUrl: time.sourceUrl,
        key: `${time.event}_${time.course}`
    }));
    const sortedEvents = [...events].sort((a, b) => compareEvents(a.event, b.event));
    // Get unique strokes and distances
    const uniqueStrokes = collectStrokes(events.map(evt => evt.event));
    const uniqueDistances = collectDistances(events.map(evt => evt.event));
    // Filter events by course, stroke, distance
    const filteredEvents = sortedEvents.filter(evt => {
        const courseMatch = courseFilter === 'all' || evt.course === courseFilter;
        const { stroke, distance } = parseEventName(evt.event);
        const strokeMatch = selectedStrokes.length === 0 || selectedStrokes.includes(stroke);
        const distanceMatch = selectedDistances.length === 0 || selectedDistances.includes(distance.toString());
        return courseMatch && strokeMatch && distanceMatch;
    });
    // Group by course
    const groupedByCourse = filteredEvents.reduce((acc, evt) => {
        if (!acc[evt.course]) {
            acc[evt.course] = [];
        }
        acc[evt.course].push(evt);
        return acc;
    }, {});
    Object.values(groupedByCourse).forEach(group => {
        group.sort((a, b) => compareEvents(a.event, b.event));
    });
    const sortedCourses = Object.keys(groupedByCourse).sort((a, b) => {
        if (a === '50m')
            return -1;
        if (b === '50m')
            return 1;
        return a.localeCompare(b);
    });
    const groupedByStroke = filteredEvents.reduce((acc, evt) => {
        const { stroke } = parseEventName(evt.event);
        if (!acc[stroke])
            acc[stroke] = [];
        acc[stroke].push(evt);
        return acc;
    }, {});
    const sortedStrokeGroups = sortStrokeValues(Object.keys(groupedByStroke));
    const activeGroups = groupBy === 'course' ? groupedByCourse : groupedByStroke;
    const activeGroupKeys = groupBy === 'course' ? sortedCourses : sortedStrokeGroups;
    /** Commit a freshly fetched map for whichever mode is active, leaving the other mode's cache be. */
    const applyFetchedRankings = (updated) => {
        if (ageMode === 'combined') {
            setCombinedRankingsData(updated);
        }
        else {
            setOwnRankings(updated);
        }
        return {
            ...swimmerData,
            selectedCountyCode: countyCode,
            selectedRegionCode: regionCode,
            rankings: ageMode === 'combined' ? Object.fromEntries(ownRankings) : Object.fromEntries(updated),
            combinedRankings: ageMode === 'combined' ? Object.fromEntries(updated) : Object.fromEntries(combinedRankingsData),
            // Dropping the key is what actually reclaims the old forecast cache, since a save
            // replaces the whole stored document.
            forecastRankings: undefined,
        };
    };
    const fetchAllRankings = async () => {
        if (!swimmerData.birthYear || !swimmerData.gender) {
            showToast('Cannot load rankings: missing birth year or gender', 'error');
            return;
        }
        const allKeys = new Set();
        events.forEach(evt => {
            allKeys.add(`${evt.key}_C`);
            if (regionCode)
                allKeys.add(`${evt.key}_R`);
            if (swimmerClubCode)
                allKeys.add(`${evt.key}_O`);
            allKeys.add(`${evt.key}_N`);
        });
        setLoadingEvents(allKeys);
        setFetchingAll(true);
        try {
            const rankingsData = await api.getSwimmerRankings(swimmerData, undefined, activeAges, countyCode, regionCode);
            const updatedRankings = new Map(rankings);
            for (const ranking of rankingsData.rankings) {
                const eventKey = `${ranking.event}_${ranking.course}`;
                const existing = updatedRankings.get(eventKey) || {};
                existing.kentCounty = { entry: ranking.kentCountyRanking, total: ranking.totalKentCounty, ageGroup: ranking.kentCountyAgeGroup, truncated: ranking.kentCountyTruncated };
                if (ranking.regionRanking !== undefined) {
                    existing.region = { entry: ranking.regionRanking, total: ranking.totalRegion ?? 0, ageGroup: ranking.regionAgeGroup, truncated: ranking.regionTruncated };
                }
                if (ranking.clubRanking !== undefined) {
                    existing.club = { entry: ranking.clubRanking, total: ranking.totalClub ?? 0, ageGroup: ranking.clubAgeGroup, truncated: ranking.clubTruncated };
                }
                existing.national = { entry: ranking.nationalRanking, total: ranking.totalNational, ageGroup: ranking.nationalAgeGroup, truncated: ranking.nationalTruncated };
                updatedRankings.set(eventKey, existing);
            }
            const updatedSwimmerData = applyFetchedRankings(updatedRankings);
            await api.saveSwimmer(updatedSwimmerData);
            onSwimmerDataChange?.(updatedSwimmerData);
            if (onRankingsSaved)
                onRankingsSaved();
            showToast(`All rankings loaded (${rankingsData.rankings.length} events)`, 'success');
        }
        catch (error) {
            showToast(`Error loading rankings: ${error.message}`, 'error');
        }
        finally {
            setLoadingEvents(new Set());
            setFetchingAll(false);
        }
    };
    const fetchRanking = async (eventKey, level) => {
        if (!swimmerData.birthYear || !swimmerData.gender) {
            showToast('Cannot load rankings: missing birth year or gender', 'error');
            return;
        }
        const event = events.find(e => e.key === eventKey);
        if (!event)
            return;
        // Mark this event as loading
        setLoadingEvents(prev => new Set(prev).add(`${eventKey}_${level}`));
        try {
            // Create a minimal SwimmerData with just this event
            const singleEventData = {
                ...swimmerData,
                times: [swimmerData.times.find(t => `${t.event}_${t.course}` === eventKey)]
            };
            const rankingsData = await api.getSwimmerRankings(singleEventData, level, activeAges, countyCode, level === 'R' ? regionCode : undefined);
            // Note: level 'O' (club) auto-derives clubCode from swimmerData.club on the backend
            if (rankingsData.rankings.length > 0) {
                const ranking = rankingsData.rankings[0];
                // Update rankings state
                const updatedRankings = new Map(rankings);
                const existing = updatedRankings.get(eventKey) || {};
                if (level === 'C') {
                    existing.kentCounty = {
                        entry: ranking.kentCountyRanking,
                        total: ranking.totalKentCounty,
                        ageGroup: ranking.kentCountyAgeGroup,
                        truncated: ranking.kentCountyTruncated
                    };
                }
                else if (level === 'R') {
                    existing.region = {
                        entry: ranking.regionRanking ?? null,
                        total: ranking.totalRegion ?? 0,
                        ageGroup: ranking.regionAgeGroup,
                        truncated: ranking.regionTruncated
                    };
                }
                else if (level === 'O') {
                    existing.club = {
                        entry: ranking.clubRanking ?? null,
                        total: ranking.totalClub ?? 0,
                        ageGroup: ranking.clubAgeGroup,
                        truncated: ranking.clubTruncated
                    };
                }
                else {
                    existing.national = {
                        entry: ranking.nationalRanking,
                        total: ranking.totalNational,
                        ageGroup: ranking.nationalAgeGroup,
                        truncated: ranking.nationalTruncated
                    };
                }
                updatedRankings.set(eventKey, existing);
                const updatedSwimmerData = applyFetchedRankings(updatedRankings);
                // Save to data store
                api.saveSwimmer(updatedSwimmerData)
                    .then(() => {
                    onSwimmerDataChange?.(updatedSwimmerData);
                    // Refresh saved swimmers list in parent component
                    if (onRankingsSaved) {
                        onRankingsSaved();
                    }
                })
                    .catch(err => {
                    console.error('Error saving rankings:', err);
                    showToast('Failed to save rankings', 'error');
                });
                const levelName = level === 'C' ? selectedCountyName : level === 'R' ? selectedRegionName : level === 'O' ? swimmerClubName : 'National';
                const hasResult = level === 'C' ? ranking.kentCountyRanking : level === 'R' ? ranking.regionRanking : level === 'O' ? ranking.clubRanking : ranking.nationalRanking;
                if (hasResult) {
                    showToast(`${levelName} ranking loaded`, 'success');
                }
                else {
                    showToast(`No ${levelName} ranking found`, 'error');
                }
            }
        }
        catch (error) {
            showToast(`Error loading ranking: ${error.message}`, 'error');
        }
        finally {
            setLoadingEvents(prev => {
                const newSet = new Set(prev);
                newSet.delete(`${eventKey}_${level}`);
                return newSet;
            });
        }
    };
    const isEventLoading = (eventKey, level) => {
        return loadingEvents.has(`${eventKey}_${level}`);
    };
    const ageModeOptions = [
        { value: 'own', label: ownAgeLabel },
        ...(combinedAgeGroup ? [{ value: 'combined', label: formatAgeGroupLabel(combinedAgeGroup) }] : []),
    ];
    const rankTableUrl = (event, course, level) => activeAges.length === 1
        ? buildRankingUrl(event, course, swimmerSex, activeAges[0], level, countyCode, regionCode, swimmerClubCode)
        : undefined;
    /**
     * One column of the desktop table. All four levels render identically, so they share this
     * rather than repeating the same forty lines with a different field plucked out of the cache.
     */
    const renderDesktopCell = (evt, cached, level) => {
        const cachedLevel = level === 'C' ? cached?.kentCounty : level === 'R' ? cached?.region : level === 'O' ? cached?.club : cached?.national;
        const entry = cachedLevel?.entry;
        const fullName = levelName[level];
        const isLoadingLevel = isEventLoading(evt.key, level);
        const approx = !!cachedLevel?.truncated;
        const tableUrl = rankTableUrl(evt.event, evt.course, level);
        return (React.createElement("td", { key: level, "data-label": fullName, className: "hide-on-mobile" },
            React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '0.5rem' } },
                React.createElement("button", { onClick: () => fetchRanking(evt.key, level), disabled: loading || isLoadingLevel, style: { padding: '0.35rem 0.6rem', fontSize: '0.8rem', flexShrink: 0, ...(cachedLevel && { background: 'transparent', border: 'none' }) }, title: cachedLevel ? 'Click to refresh' : 'Fetch ranking' }, isLoadingLevel ? React.createElement(FontAwesomeIcon, { icon: faSpinner, spin: true }) : cachedLevel ? React.createElement(FontAwesomeIcon, { icon: faRotate, style: { color: 'var(--success)' } }) : 'Fetch'),
                cachedLevel ? (entry ? (React.createElement("div", { style: { flex: 1 } },
                    React.createElement("strong", { title: approx ? 'Merged from lists capped at 100 per age, so this is a minimum' : undefined },
                        entry.isTied ? '≈' : '',
                        approx ? '≥' : '',
                        "#",
                        entry.position),
                    " of ",
                    formatTotal(cachedLevel.total, approx),
                    entry.fina && React.createElement("span", null,
                        " \u00B7 ",
                        React.createElement("strong", null, entry.fina),
                        " pts"),
                    React.createElement("br", null),
                    React.createElement("small", { style: { color: 'var(--gray-400)' } },
                        entry.time,
                        " \u00B7 ",
                        entry.date,
                        tableUrl && (React.createElement("a", { href: tableUrl, target: "_blank", rel: "noreferrer", className: "stroke-link", style: { marginLeft: '0.4rem' }, title: `Open the ${fullName} ranking table` },
                            React.createElement(FontAwesomeIcon, { icon: faArrowUpRightFromSquare })))))) : (React.createElement("span", { style: { color: 'var(--gray-400)' } }, "Not ranked"))) : (React.createElement("span", { style: { color: 'var(--gray-400)', fontStyle: 'italic' } }, "Click to fetch")))));
    };
    return (React.createElement("div", { style: { position: 'relative' } },
        fetchingAll && React.createElement(RankingsLoadingOverlay, { eventCount: events.length, ageLabel: activeAgeLabel, ageCount: activeAges.length }),
        React.createElement(EventFilterBar, { strokes: uniqueStrokes, selectedStrokes: selectedStrokes, onStrokesChange: onSelectedStrokesChange, distances: uniqueDistances, selectedDistances: selectedDistances, onDistancesChange: onSelectedDistancesChange, groupBy: groupBy, onGroupByChange: setGroupBy, disabled: loading },
            swimmerData.birthYear && (React.createElement("div", { className: "rankings-age-controls" },
                React.createElement(SegmentedToggle, { options: ageModeOptions, value: ageMode, onChange: next => {
                        localStorage.setItem(ageModeKey(swimmerData.tiref), next);
                        setAgeMode(next);
                    }, disabled: loading, label: "Age", trailing: onCombinedAgeGroupChange && (React.createElement(CombinedAgeGroupEditor, { ownAge: swimmerAge, value: combinedAgeGroup, onChange: onCombinedAgeGroupChange, disabled: loading || loadingEvents.size > 0 })) }))),
            React.createElement("div", { style: { alignSelf: 'center', marginLeft: 'auto' } },
                React.createElement("button", { className: "btn-fetch", onClick: fetchAllRankings, disabled: loading || loadingEvents.size > 0, style: { padding: '0.35rem 0.75rem', fontSize: '0.85rem' }, title: `Fetch all rankings for age ${activeAgeLabel}` }, loadingEvents.size > 0 ? React.createElement(React.Fragment, null,
                    React.createElement(FontAwesomeIcon, { icon: faSpinner, spin: true }),
                    " Fetching\u2026") : React.createElement(React.Fragment, null,
                    React.createElement(FontAwesomeIcon, { icon: faCloudDownload }),
                    " Fetch All\u2026")))),
        React.createElement("div", { style: { marginBottom: '10px', padding: '10px', backgroundColor: 'var(--card-bg)', borderRadius: '5px' } },
            React.createElement("strong", null, "Age:\u00A0"),
            swimmerData.birthYear && (React.createElement(React.Fragment, null,
                activeAgeLabel,
                React.createElement("span", { style: { marginLeft: '0.5rem', color: 'var(--gray-400)', fontSize: '0.85rem' } }, `(as at Dec ${new Date().getFullYear()})`),
                ageMode === 'combined' && (React.createElement("span", { style: { marginLeft: '0.5rem', color: 'var(--gray-400)', fontSize: '0.85rem' } },
                    "- merged from ",
                    activeAges.length,
                    " separate age lists combined into 1 set of rankings.")),
                combinedAgedOut && (React.createElement("span", { style: { marginLeft: '0.5rem', color: 'var(--warning, var(--primary))', fontSize: '0.85rem' } },
                    "- your saved ",
                    formatAgeGroupLabel(swimmerData.combinedAgeGroup),
                    " group no longer includes age ",
                    swimmerAge,
                    "; pick a new one"))))),
        filteredEvents.length === 0 ? (React.createElement("div", { className: "empty-state" },
            React.createElement("p", null, "No events match the current filters"))) : (activeGroupKeys.map((groupKey, courseIndex) => (React.createElement("div", { key: groupKey, className: `club-group${courseIndex > 0 ? ' club-group-spaced' : ''}` },
            React.createElement("div", { className: "club-header", onClick: () => toggleCourse(groupKey) },
                React.createElement("h3", { className: "club-header-title" },
                    React.createElement(FontAwesomeIcon, { icon: faPersonSwimming }),
                    " ",
                    groupLabel(groupBy, groupKey)),
                React.createElement(FontAwesomeIcon, { icon: faChevronDown, className: `chevron-icon${!collapsedCourses.has(groupKey) ? ' expanded' : ''}` })),
            !collapsedCourses.has(groupKey) && React.createElement("div", { className: "table-container rankings-cards" },
                React.createElement("table", null,
                    React.createElement("thead", null,
                        React.createElement("tr", null,
                            React.createElement("th", null, "Event"),
                            React.createElement("th", null, "PB Time"),
                            React.createElement("th", null, selectedCountyName),
                            React.createElement("th", null, selectedRegionName),
                            React.createElement("th", null, swimmerClubName),
                            React.createElement("th", null, "National"))),
                    React.createElement("tbody", null, activeGroups[groupKey].map((evt) => {
                        const cached = rankings.get(evt.key);
                        // FINA points come off the swimmer's own row, which is the same performance at
                        // every level, so the card shows them once instead of repeating them per pill.
                        const cardFina = [cached?.club, cached?.region, cached?.kentCounty, cached?.national]
                            .find(level => level?.entry?.fina)?.entry?.fina;
                        /**
                         * One cell of the mobile card's rank grid. The pill itself is the fetch/refresh
                         * control - an unfetched level invites a tap, a fetched one re-fetches - which
                         * keeps the card free of a button per level. When a rank exists a small
                         * external-link icon sits beside it for the full ranking table.
                         */
                        const renderRankCell = (level) => {
                            const cachedLevel = level === 'C' ? cached?.kentCounty : level === 'R' ? cached?.region : level === 'O' ? cached?.club : cached?.national;
                            const entry = cachedLevel?.entry;
                            const fullName = levelName[level];
                            const rankUrl = rankTableUrl(evt.event, evt.course, level);
                            const isLoadingLevel = isEventLoading(evt.key, level);
                            const strong = !!entry && entry.position <= STRONG_POSITION;
                            const approx = !!cachedLevel?.truncated;
                            const pillClass = isLoadingLevel
                                ? 'rank-pill is-loading'
                                : !cachedLevel
                                    ? 'rank-pill is-empty'
                                    : !entry
                                        ? 'rank-pill is-unranked'
                                        : strong
                                            ? 'rank-pill is-strong'
                                            : 'rank-pill';
                            return (React.createElement("div", { className: "rank-cell", key: level },
                                React.createElement("span", { className: "rank-cell-label", title: fullName }, fullName),
                                React.createElement("span", { className: "rank-cell-value" },
                                    React.createElement("button", { type: "button", className: pillClass, onClick: () => fetchRanking(evt.key, level), disabled: loading || isLoadingLevel, title: approx ? `${fullName} - ranked against a list capped at 100 per age, so this is a minimum. Tap to refresh`
                                            : cachedLevel ? `${fullName} - tap to refresh`
                                                : `Fetch ${fullName} ranking`, "aria-label": isLoadingLevel ? `Loading ${fullName} ranking`
                                            : !cachedLevel ? `Fetch ${fullName} ranking`
                                                : !entry ? `Not ranked in ${fullName} — tap to refresh`
                                                    : `${fullName}: ${entry.isTied ? 'tied ' : ''}position ${approx ? 'at least ' : ''}${entry.position} of ${formatTotal(cachedLevel.total, approx)} - tap to refresh` }, isLoadingLevel ? (React.createElement(FontAwesomeIcon, { icon: faSpinner, spin: true })) : !cachedLevel ? (React.createElement(React.Fragment, null,
                                        React.createElement(FontAwesomeIcon, { icon: faCloudDownload }),
                                        " Fetch")) : !entry ? ('-') : (React.createElement(React.Fragment, null,
                                        entry.isTied ? '=' : '',
                                        entry.position,
                                        " ",
                                        React.createElement("span", { className: "rank-pill-total" },
                                            "/ ",
                                            formatTotal(cachedLevel.total, approx))))),
                                    entry && rankUrl && (React.createElement("a", { className: "rank-cell-link", href: rankUrl, target: "_blank", rel: "noreferrer", title: `Open the ${fullName} ranking table`, "aria-label": `Open the ${fullName} ranking table` },
                                        React.createElement(FontAwesomeIcon, { icon: faArrowUpRightFromSquare }))))));
                        };
                        return (React.createElement("tr", { key: evt.key },
                            React.createElement("td", { "data-label": "Event", className: "card-event-td" },
                                React.createElement("div", { className: "card-event-header" },
                                    React.createElement("div", null,
                                        groupBy !== 'course' && (React.createElement(React.Fragment, null,
                                            React.createElement("span", { className: "swimmer-tag-badge hide-on-mobile" }, evt.course === '50m' ? 'LC' : 'SC'),
                                            React.createElement("span", { className: "hide-on-mobile" }, "\u00A0\u00A0"))),
                                        evt.event,
                                        React.createElement("span", null, evt.sourceUrl && (React.createElement(React.Fragment, null,
                                            "\u00A0\u00A0",
                                            React.createElement("a", { className: "stroke-link", href: evt.sourceUrl, target: "_blank", rel: "noreferrer" },
                                                React.createElement(FontAwesomeIcon, { icon: faArrowUpRightFromSquare })))))),
                                    React.createElement("span", { className: "mobile-supplement card-inline-time" }, evt.time)),
                                React.createElement("div", { className: "mobile-supplement rank-card-body" },
                                    React.createElement("div", { className: "rank-card-sub" },
                                        React.createElement("span", { className: "rank-card-course" }, courseLabel(evt.course)),
                                        React.createElement("span", { className: "rank-card-sub-right" },
                                            cardFina && React.createElement("span", { className: "rank-card-fina" },
                                                cardFina,
                                                " pts"),
                                            React.createElement("span", null, evt.date))),
                                    React.createElement("div", { className: "rank-card-grid" }, CARD_LEVELS.map(renderRankCell)))),
                            React.createElement("td", { "data-label": "PB Time", className: "hide-on-mobile" },
                                evt.time,
                                React.createElement("br", null),
                                React.createElement("small", { style: { color: 'var(--gray-400)' } }, evt.date),
                                React.createElement("br", null),
                                React.createElement("small", { style: { color: 'var(--gray-400)' } },
                                    "Age: ",
                                    swimmerAge)),
                            CARD_LEVELS_DESKTOP.map(level => renderDesktopCell(evt, cached, level))));
                    })))))))),
        React.createElement("div", { style: { marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--card-bg)', borderRadius: '4px' } },
            React.createElement("p", { style: { fontSize: '0.9rem', color: 'var(--gray-400)', margin: 0 } },
                React.createElement("strong", null, "Note:"),
                " Click \"Fetch\" on any level to load that ranking, or \"Fetch All\" for every event at once. Rankings are based on the last 12 months of performances and are limited to the club, county or region selected for this swimmer; National includes all swimmers in the UK. Add a combined age group to also rank against more than one year at a time - those positions are merged from one list per age, each capped at 100 entries, so totals shown with a ",
                React.createElement("strong", null, "+"),
                " are minimums."))));
};
export default Rankings;
